import {
  Box,
  Heading,
  HStack,
  Flex,
  Text,
  SimpleGrid,
  Image,
  Icon,
  Link,
} from "@chakra-ui/react";
import { FaFacebook, FaInstagram } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { trackAppDownloadClick } from "../core/analytics";

import Logo from "../assets/images/logo.png";

const socials = [
  {
    id: 1,
    icon: <FaFacebook />,
    url: "https://www.facebook.com/getbookd.uk",
  },
  {
    id: 2,
    icon: <FaInstagram />,
    url: "https://www.instagram.com/get_bookd?igsh=MXN2dXBkcXZocnptMw%3D%3D&utm_source=qr",
  },
];

type FooterProps = {
  appType?: "stylist" | "client";
};

export const Footer = ({ appType }: FooterProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isClientRoute = location.pathname === "/client";
  const resolvedAppType = appType ?? (isClientRoute ? "client" : "stylist");

  const storeLinks = {
    stylist: {
      appStore: "https://apps.apple.com/us/app/bookd-pro/id6503949063",
      playStore: "https://play.google.com/store/apps/details?id=com.book_d&pcampaignid=web_share",
    },
    client: {
      appStore: "https://apps.apple.com/us/app/book-d-find-beauty-pros/id6558535790",
      playStore: "https://play.google.com/store/apps/details?id=com.bookd.customer",
    },
  };

  const links = storeLinks[resolvedAppType];

  return (
    <Box
      px={{ base: "16px", lg: "150px" }}
      pt={{ base: "40px", lg: "90px" }}
      pb="123px"
      maxW={{ base: "100%", "2xl": "1700px" }}
      mx="auto"
    >
      <Flex flexDir={"column"} gap={"20px"} align={"center"}>
        <Heading
          fontSize={"14px"}
          fontWeight={700}
          color={"#0A142F"}
          fontFamily={"LeagueSpartan"}
        >
          BOOK CONVENIENTLY
        </Heading>

        <Heading
          fontSize={{ base: "32px", lg: "64px" }}
          color={"#19202D"}
          fontFamily={"LeagueSpartan"}
        >
          Get Book-d
        </Heading>

        <Text
          fontSize={{ base: "16px", lg: "18px" }}
          color={"#19202D"}
          fontWeight={500}
          textAlign={"center"}
        >
          A quick and easy way to book beauty professionals in your area and
          beyond.
        </Text>

        <HStack gap={"24px"}>
          <Flex
            borderRadius={"30px"}
            py="10px"
            px={{ base: "14px", lg: "20px" }}
            bg="#19202D"
            gap={"10px"}
            align={"center"}
            cursor={"pointer"}
            onClick={() => {
              trackAppDownloadClick("app_store", appType);
              window.open(links.appStore, "_blank");
            }}
          >
            <svg width="32" height="33" viewBox="0 0 32 33" fill="none">
              <path
                d="M15.6041 7.98736C15.643 6.78146 15.9752 5.00501 17.1312 3.83797C18.2042 2.75466 19.5105 2.22668 20.3241 1.98531C20.6383 1.89212 20.9396 2.12898 20.9241 2.45625C20.8696 3.61065 20.5661 5.3325 19.7829 6.25493C19.0446 7.12448 17.857 8.27756 16.1174 8.47157C15.8327 8.50333 15.5949 8.27371 15.6041 7.98736Z"
                fill="white"
              />
              <path
                d="M18.5828 30.0592C19.4182 30.3792 20.2628 30.7027 21.0272 30.7027C22.6624 30.7027 27.4238 26.93 27.5953 23.0116C27.6042 22.8104 27.4839 22.6307 27.3082 22.5323C26.097 21.8545 24.1192 20.167 24.0504 17.7119C23.9701 14.8507 25.4067 12.5782 26.4939 11.6382C26.7386 11.4266 26.8226 11.0536 26.6042 10.815C25.615 9.73474 23.6249 8.2488 21.8092 8.28528C20.4882 8.31182 19.298 8.80388 18.2767 9.22613C17.4932 9.55008 16.809 9.83293 16.2414 9.83293C15.7039 9.83293 15.0585 9.55189 14.3178 9.22934C13.3204 8.79496 12.15 8.28528 10.8371 8.28528C8.40516 8.28528 3.60011 10.3019 3.6001 18.0871C3.60009 22.2141 7.86057 30.8122 11.4557 30.7027C12.232 30.7027 13.0617 30.3813 13.8849 30.0623C14.6956 29.7482 15.5001 29.4365 16.2414 29.4365C16.9572 29.4365 17.7656 29.7462 18.5828 30.0592Z"
                fill="white"
              />
            </svg>

            <Text
              fontSize={{ base: "14px", lg: "22px" }}
              mt="4px"
              fontWeight={600}
              color="#fff"
              lineHeight={"100%"}
              fontFamily={"LeagueSpartan"}
            >
              APP STORE
            </Text>
          </Flex>

          <Flex
            borderRadius={"30px"}
            py="10px"
            px={{ base: "14px", lg: "20px" }}
            bg="#19202D"
            gap={"10px"}
            align={"center"}
            cursor={"pointer"}
            onClick={() => {
              trackAppDownloadClick("play_store", appType);
              window.open(links.playStore, "_blank");
            }}
          >
            <svg width="32" height="33" viewBox="0 0 32 33" fill="none">
              <path
                d="M9.2986 27.1325C9.18978 27.2411 9.32002 27.4178 9.45528 27.3446C12.9062 25.4779 17.5654 22.9559 21.503 20.8174C21.5816 20.7747 21.5961 20.6684 21.5323 20.6056L18.7577 17.8737C18.7055 17.8224 18.6217 17.8227 18.5699 17.8744L9.2986 27.1325Z"
                fill="white"
              />
              <path
                d="M18.5614 14.8687C18.6135 14.9203 18.6976 14.9202 18.7495 14.8683L21.5251 12.0952C21.5883 12.0321 21.5728 11.9259 21.4942 11.8835L20.1342 11.1494C18.499 10.2668 16.3078 9.08422 14.084 7.88448C12.4186 6.98599 10.7366 6.07879 9.25636 5.28086C9.12082 5.2078 8.98996 5.38456 9.09933 5.49293L18.5614 14.8687Z"
                fill="white"
              />
              <path
                d="M4 4.76227C4 4.45252 4.04686 4.1932 4.12838 3.97959C4.21262 3.75884 4.49069 3.74193 4.65853 3.90823L17.1447 16.2805C17.1972 16.3326 17.1974 16.4174 17.145 16.4697L4.61341 28.964C4.4288 29.1481 4.12365 29.1035 4.06461 28.8496C4.02175 28.6653 4 28.4609 4 28.2413V4.76227Z"
                fill="white"
              />
              <path
                d="M23.5842 19.6855C23.5327 19.7135 23.4688 19.7045 23.427 19.6634L20.1765 16.4637C20.1236 16.4117 20.1234 16.3265 20.176 16.2742L23.4441 13.0252C23.486 12.9836 23.5506 12.9746 23.6024 13.0029C26.3841 14.52 28.554 15.698 29.2156 16.0479C29.2403 16.061 29.2595 16.0808 29.2707 16.1065C29.3311 16.2457 29.345 16.3841 29.3244 16.5173C29.3185 16.5558 29.2935 16.5875 29.2591 16.6056C28.8785 16.8062 27.6045 17.4992 25.8298 18.4644L25.8189 18.4704C25.1397 18.8398 24.3875 19.2489 23.5842 19.6855Z"
                fill="white"
              />
            </svg>

            <Text
              fontSize={{ base: "14px", lg: "22px" }}
              mt="4px"
              fontWeight={600}
              color="#fff"
              lineHeight={"100%"}
              fontFamily={"LeagueSpartan"}
            >
              GOOGLE PLAY
            </Text>
          </Flex>
        </HStack>

        <Box h={"1px"} w={"100%"} opacity={0.06} bg="#0A142F" mt="60px" />

        <SimpleGrid
          columns={{ base: 1, lg: 3 }}
          w="100%"
          mt="4px"
          alignItems={"center"}
          gap={"20px"}
        >
          <Image
            src={Logo}
            h={"48px"}
            objectFit={"contain"}
            onClick={() => navigate("/")}
            cursor={"pointer"}
            justifySelf={{ base: "center", lg: "flex-start" }}
          />

          <HStack gap={"60px"} justifySelf={"center"}>
            <Link
              fontSize={"16px"}
              color={"#0A142F"}
              fontFamily={"LeagueSpartan"}
              onClick={() => navigate("/terms")}
              cursor="pointer"
            >
              Terms
            </Link>

            <Link
              fontSize={"16px"}
              color={"#0A142F"}
              fontFamily={"LeagueSpartan"}
              onClick={() => navigate("/privacy")}
              cursor="pointer"
            >
              Privacy
            </Link>

            {!isClientRoute && (
              <Link
                fontSize={"16px"}
                color={"#0A142F"}
                fontFamily={"LeagueSpartan"}
                onClick={() => navigate("/pricing")}
                cursor="pointer"
              >
                Pricing
              </Link>
            )}

            <Link
              fontSize={"16px"}
              color={"#0A142F"}
              fontFamily={"LeagueSpartan"}
            >
              Help
            </Link>
          </HStack>

          <HStack gap="20px" justifySelf={{ base: "center", lg: "flex-end" }}>
            {socials.map((item) => (
              <Flex
                key={item.id}
                w={"40px"}
                h="40px"
                borderWidth={"2.25px"}
                borderRadius={"full"}
                borderColor={"rgba(10, 20, 47, 0.1)"}
                align={"center"}
                justify={"center"}
                cursor={"pointer"}
                onClick={() => window.open(item.url, "_blank")}
              >
                <Icon size={"sm"} color={"#0A142F"}>
                  {item.icon}
                </Icon>
              </Flex>
            ))}
          </HStack>
        </SimpleGrid>
      </Flex>
    </Box>
  );
};
